import { Capacitor } from '@capacitor/core'
import type { Order } from '../types'
import { getSettings } from './settingsStorage'
import { playNewOrderChime } from './newOrderChime'
import { getCurrentPlaceId } from './placeConfig'
import { primeOrdersCacheIfNeeded } from './orderStorage'
import { getLocalDeviceId } from './deviceStorage'
import { markOrdersAlerted } from './orderAlertDedup'
import { getFirestoreInstance, useFirestore } from './firebase'
import { deletePushToken, upsertPushToken } from './firestore/pushTokens'
import { checkBlePermissionGranted, promptForBlePermissions } from './blePermissions'
import { CHANNEL_SOUND_CONFIRM_KEY } from './nativeSetupGate'

const ORDER_ALERTS_CHANNEL_ID = 'order-alerts'
const LISTENING_CHANNEL_ID = 'pos-listening'
const FG_SERVICE_NOTIFICATION_ID = 1001

/** Android FOREGROUND_SERVICE_TYPE_DATA_SYNC (not yet in plugin ServiceType enum). */
const FG_SERVICE_TYPE_DATA_SYNC = 1 as import(
  '@capawesome-team/capacitor-android-foreground-service'
).ServiceType

export type OrderAlertReadiness = {
  notifications: 'granted' | 'denied' | 'prompt'
  bluetooth: 'granted' | 'denied' | 'unknown'
  batteryOptimized: boolean | null
  serviceRunning: boolean
  channelSoundConfirmed: boolean
  channelNeedsReview: boolean
  channelGuidance: string
  ready: boolean
  issues: string[]
}

let initPromise: Promise<void> | null = null
let serviceRunning = false
let lastRuntimeError: string | null = null
let resumeListenerAttached = false
let activityListenerAttached = false
let pushListenersAttached = false
let pushTokenListenerAttached = false
let activePushPlaceId: string | null = null

/** True when POS UI is in the foreground (MainActivity bridge + fallbacks). */
let appUiActive = true
let uiBecameActiveAt: number | null = null
const CATCH_UP_MS = 3000

export function isAppUiActive(): boolean {
  return appUiActive
}

function isWithinCatchUpWindow(): boolean {
  return uiBecameActiveAt !== null && Date.now() - uiBecameActiveAt < CATCH_UP_MS
}

function setAppUiActive(active: boolean): void {
  const prev = appUiActive
  if (active && !appUiActive) {
    uiBecameActiveAt = Date.now()
  }
  if (!active) {
    uiBecameActiveAt = null
  }
  appUiActive = active
  if (prev !== active) {
    console.info(`[order-alerts] appUiActive=${active ? 'foreground' : 'background'}`)
  }
}

function parseActivityStateDetail(detail: unknown): boolean | null {
  if (typeof detail === 'boolean') return detail
  if (detail && typeof detail === 'object' && 'active' in detail) {
    return !!(detail as { active: unknown }).active
  }
  if (typeof detail === 'string') {
    try {
      const parsed = JSON.parse(detail) as { active?: unknown }
      return !!parsed.active
    } catch {
      return null
    }
  }
  return null
}

function initActivityStateListener(): void {
  if (activityListenerAttached) return
  activityListenerAttached = true

  window.addEventListener('wortelogyActivityState', (event: Event) => {
    const active = parseActivityStateDetail((event as CustomEvent).detail)
    if (active !== null) setAppUiActive(active)
  })

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      setAppUiActive(document.visibilityState === 'visible')
    })
  }

  void import('@capacitor/app').then(({ App }) => {
    void App.addListener('appStateChange', ({ isActive }) => {
      setAppUiActive(isActive)
    })
  })
}

function isAndroidNative(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android'
}

function isNativePushSupported(): boolean {
  return Capacitor.isNativePlatform() && (Capacitor.getPlatform() === 'android' || Capacitor.getPlatform() === 'ios')
}

function readOrderIdFromPushPayload(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') return null
  const data = (payload as { data?: Record<string, unknown> }).data
  const fromData = data?.orderId
  if (typeof fromData === 'string' && fromData.trim()) return fromData.trim()
  const direct = (payload as { orderId?: unknown }).orderId
  if (typeof direct === 'string' && direct.trim()) return direct.trim()
  return null
}

function formatOrderAlertBody(orders: Order[]): string {
  const first = orders[0]
  if (!first) return 'Tap to open POS'
  const detail = first.displayNumber
    ? `Order #${first.displayNumber}`
    : first.tableOrLocation
      ? `Table ${first.tableOrLocation}`
      : 'New order received'
  if (orders.length === 1) return detail
  return `${detail} and ${orders.length - 1} more`
}

function setRuntimeError(message: string | null): void {
  lastRuntimeError = message
}

function getChannelSoundConfirmed(): boolean {
  if (typeof localStorage === 'undefined') return false
  try {
    return localStorage.getItem(CHANNEL_SOUND_CONFIRM_KEY) === '1'
  } catch {
    return false
  }
}

export function markOrderAlertChannelConfirmed(confirmed: boolean): void {
  if (typeof localStorage === 'undefined') return
  try {
    if (confirmed) {
      localStorage.setItem(CHANNEL_SOUND_CONFIRM_KEY, '1')
    } else {
      localStorage.removeItem(CHANNEL_SOUND_CONFIRM_KEY)
    }
  } catch {
    // ignore storage errors
  }
}

export function getLastRuntimeError(): string | null {
  return lastRuntimeError
}

/** Whether the order-alert Android foreground service is currently running. */
export function isOrderAlertServiceRunning(): boolean {
  return serviceRunning
}

async function attachResumeListener(): Promise<void> {
  if (resumeListenerAttached || !isAndroidNative()) return
  resumeListenerAttached = true
  const { App } = await import('@capacitor/app')
  await App.addListener('appStateChange', ({ isActive }) => {
    if (isActive) {
      void primeOrdersCacheIfNeeded()
      void ensureServiceRunning()
    }
  })
}

async function ensureServiceRunning(): Promise<void> {
  if (!isAndroidNative() || serviceRunning) return
  try {
    await startOrderAlertService()
  } catch {
    // startOrderAlertService records lastRuntimeError
  }
}

async function ensureChannelsAndListeners(): Promise<void> {
  const { LocalNotifications } = await import('@capacitor/local-notifications')
  const { ForegroundService, Importance } = await import(
    '@capawesome-team/capacitor-android-foreground-service'
  )

  await LocalNotifications.createChannel({
    id: ORDER_ALERTS_CHANNEL_ID,
    name: 'Order alerts',
    description: 'Sound and notifications for new orders',
    importance: 4,
    sound: 'wortelogy_chime.mp3',
    vibration: true,
  })

  await ForegroundService.createNotificationChannel({
    id: LISTENING_CHANNEL_ID,
    name: 'POS background service',
    description: 'Keeps Wortelogy POS listening for orders',
    importance: Importance.Low,
  })

  await LocalNotifications.addListener('localNotificationActionPerformed', () => {
    const placeId = getCurrentPlaceId()
    if (!placeId) return
    window.location.assign(`/app/${placeId}/orders`)
  })

  initActivityStateListener()
  await attachResumeListener()
}

async function upsertPushTokenForPlace(
  placeId: string,
  token: string,
  userEmail?: string,
): Promise<void> {
  if (!useFirestore()) return
  const platform = Capacitor.getPlatform()
  if (platform !== 'android' && platform !== 'ios') return
  const tokenId = getLocalDeviceId()
  await upsertPushToken(placeId, getFirestoreInstance(), tokenId, {
    token,
    platform,
    deviceId: tokenId,
    updatedAt: new Date().toISOString(),
    appVersion: typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : undefined,
    lastUserEmail: userEmail,
    enabled: true,
  })
}

async function initPushListeners(): Promise<void> {
  if (!isNativePushSupported()) return
  const { FirebaseMessaging } = await import('@capacitor-firebase/messaging')

  if (!pushListenersAttached) {
    pushListenersAttached = true
    await FirebaseMessaging.addListener('notificationReceived', ({ notification }) => {
      const placeId = activePushPlaceId
      if (placeId) {
        const orderId = readOrderIdFromPushPayload(notification)
        if (orderId) {
          markOrdersAlerted(placeId, [orderId])
        }
      }
      if (isAppUiActive()) {
        const volume = getSettings().newOrderChimeVolume
        if (volume > 0) playNewOrderChime()
      }
    })

    await FirebaseMessaging.addListener('notificationActionPerformed', ({ notification }) => {
      const actionPlaceId =
        notification &&
        typeof notification === 'object' &&
        notification.data &&
        typeof notification.data === 'object' &&
        typeof (notification.data as Record<string, unknown>).placeId === 'string'
          ? ((notification.data as Record<string, unknown>).placeId as string)
          : null
      const placeId =
        activePushPlaceId ||
        actionPlaceId ||
        getCurrentPlaceId()
      if (!placeId) return
      window.location.assign(`/app/${placeId}/orders`)
    })
  }

  if (!pushTokenListenerAttached) {
    pushTokenListenerAttached = true
    await FirebaseMessaging.addListener('tokenReceived', ({ token }) => {
      const placeId = activePushPlaceId
      if (!placeId || !token) return
      void upsertPushTokenForPlace(placeId, token)
    })
  }
}

export async function registerPushTokenForPlace(
  placeId: string,
  userEmail?: string,
): Promise<void> {
  if (!isNativePushSupported() || !placeId) return
  activePushPlaceId = placeId
  try {
    const { FirebaseMessaging } = await import('@capacitor-firebase/messaging')
    await initPushListeners()

    const perm = await FirebaseMessaging.requestPermissions()
    if (perm.receive !== 'granted') {
      console.info('[order-alerts] push permission not granted yet')
      return
    }

    const tokenResult = await FirebaseMessaging.getToken()
    if (!tokenResult.token) return
    await upsertPushTokenForPlace(placeId, tokenResult.token, userEmail)
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err)
    console.error('[order-alerts] push token registration failed:', err)
    setRuntimeError(`Push token registration failed: ${msg}`)
  }
}

export async function unregisterPushTokenForPlace(placeId: string): Promise<void> {
  if (!isNativePushSupported() || !placeId || !useFirestore()) return
  try {
    const tokenId = getLocalDeviceId()
    await deletePushToken(placeId, getFirestoreInstance(), tokenId)
    if (activePushPlaceId === placeId) activePushPlaceId = null
  } catch (err) {
    console.error('[order-alerts] push token cleanup failed:', err)
  }
}

async function ensureInitialized(): Promise<void> {
  if (!isAndroidNative()) return
  if (!initPromise) {
    initPromise = ensureChannelsAndListeners().catch((err) => {
      initPromise = null
      throw err
    })
  }
  await initPromise
}

export async function checkOrderAlertReadiness(): Promise<OrderAlertReadiness> {
  if (!isAndroidNative()) {
    return {
      notifications: 'granted',
      bluetooth: 'granted',
      batteryOptimized: null,
      serviceRunning: false,
      channelSoundConfirmed: true,
      channelNeedsReview: false,
      channelGuidance: '',
      ready: true,
      issues: [],
    }
  }

  let notifications: OrderAlertReadiness['notifications'] = 'prompt'
  let batteryOptimized: boolean | null = null
  const channelSoundConfirmed = getChannelSoundConfirmed()
  const channelGuidance =
    'Open "Order alerts" channel and choose alerting mode with sound/vibration. OEM labels vary: "Alerting", "Sound", or "Allow sound and vibration".'
  const issues: string[] = []

  try {
    const { LocalNotifications } = await import('@capacitor/local-notifications')
    const perms = await LocalNotifications.checkPermissions()
    notifications =
      perms.display === 'granted'
        ? 'granted'
        : perms.display === 'denied'
          ? 'denied'
          : 'prompt'
  } catch {
    notifications = 'prompt'
    issues.push('Could not check notification permission.')
  }

  if (notifications !== 'granted') {
    issues.push('Notifications are not allowed.')
  }

  const bluetoothGranted = await checkBlePermissionGranted()
  const bluetooth: OrderAlertReadiness['bluetooth'] = bluetoothGranted
    ? 'granted'
    : 'unknown'
  if (!bluetoothGranted) {
    issues.push('Nearby devices (Bluetooth) permission is not granted.')
  }

  try {
    const { BatteryOptimization } = await import(
      '@capawesome-team/capacitor-android-battery-optimization'
    )
    const { enabled } = await BatteryOptimization.isBatteryOptimizationEnabled()
    batteryOptimized = enabled
    if (enabled) {
      issues.push('Battery optimization is on — set to Unrestricted.')
    }
  } catch {
    batteryOptimized = null
  }

  if (!serviceRunning) {
    issues.push('Background listener is not running.')
    if (lastRuntimeError) {
      issues.push(lastRuntimeError)
    }
  }

  if (!channelSoundConfirmed) {
    issues.push('Notification channel sound is not confirmed yet. Play the test alert and confirm you can hear it.')
  }

  const ready =
    notifications === 'granted' &&
    bluetooth === 'granted' &&
    batteryOptimized === false &&
    serviceRunning &&
    channelSoundConfirmed

  return {
    notifications,
    bluetooth,
    batteryOptimized,
    serviceRunning,
    channelSoundConfirmed,
    channelNeedsReview: !channelSoundConfirmed,
    channelGuidance,
    ready,
    issues,
  }
}

export async function promptForNotifications(): Promise<boolean> {
  if (!isAndroidNative()) return true
  await ensureInitialized()
  const { LocalNotifications } = await import('@capacitor/local-notifications')
  const current = await LocalNotifications.checkPermissions()
  if (current.display === 'granted') return true
  const result = await LocalNotifications.requestPermissions()
  return result.display === 'granted'
}

export async function openOrderAlertAppNotificationSettings(): Promise<void> {
  if (!isAndroidNative()) return
  try {
    const { LocalNotifications } = await import('@capacitor/local-notifications')
    const maybeLocalNotifications = LocalNotifications as unknown as {
      openSettings?: () => Promise<void>
    }
    if (typeof maybeLocalNotifications.openSettings === 'function') {
      await maybeLocalNotifications.openSettings()
      return
    }
  } catch {
    // fall back to App settings
  }
  const { App } = await import('@capacitor/app')
  const maybeApp = App as unknown as { openSettings?: () => Promise<void> }
  if (typeof maybeApp.openSettings === 'function') {
    await maybeApp.openSettings()
  }
}

export async function openOrderAlertChannelSettings(): Promise<void> {
  if (!isAndroidNative()) return
  try {
    const { LocalNotifications } = await import('@capacitor/local-notifications')
    const maybeLocalNotifications = LocalNotifications as unknown as {
      openChannelSettings?: (options: { id: string }) => Promise<void>
      openSettings?: () => Promise<void>
    }
    if (typeof maybeLocalNotifications.openChannelSettings === 'function') {
      await maybeLocalNotifications.openChannelSettings({ id: ORDER_ALERTS_CHANNEL_ID })
      return
    }
    if (typeof maybeLocalNotifications.openSettings === 'function') {
      await maybeLocalNotifications.openSettings()
      return
    }
  } catch {
    // fall back to App settings
  }
  const { App } = await import('@capacitor/app')
  const maybeApp = App as unknown as { openSettings?: () => Promise<void> }
  if (typeof maybeApp.openSettings === 'function') {
    await maybeApp.openSettings()
  }
}

export async function promptForBatteryUnrestricted(): Promise<boolean> {
  if (!isAndroidNative()) return true
  try {
    const { BatteryOptimization } = await import(
      '@capawesome-team/capacitor-android-battery-optimization'
    )
    const { enabled } = await BatteryOptimization.isBatteryOptimizationEnabled()
    if (!enabled) return true
    try {
      await BatteryOptimization.requestIgnoreBatteryOptimization()
    } catch {
      await BatteryOptimization.openBatteryOptimizationSettings()
      return false
    }
    const after = await BatteryOptimization.isBatteryOptimizationEnabled()
    if (after.enabled) {
      await BatteryOptimization.openBatteryOptimizationSettings()
      return false
    }
    return true
  } catch {
    return false
  }
}

export async function fixAllOrderAlertPermissions(): Promise<OrderAlertReadiness> {
  if (!isAndroidNative()) {
    return checkOrderAlertReadiness()
  }
  await promptForNotifications()
  await promptForBlePermissions()
  await promptForBatteryUnrestricted()
  await ensureServiceRunning()
  return checkOrderAlertReadiness()
}

export async function sendTestOrderAlert(): Promise<void> {
  if (!isAndroidNative()) return
  await ensureInitialized()
  const { LocalNotifications } = await import('@capacitor/local-notifications')
  const id = (Date.now() % 2147483646) + 1
  await LocalNotifications.schedule({
    notifications: [
      {
        id,
        title: 'Order alert test',
        body: 'You should hear the Wortelogy chime sound.',
        channelId: ORDER_ALERTS_CHANNEL_ID,
        smallIcon: 'ic_stat_order',
        sound: 'wortelogy_chime.mp3',
        autoCancel: true,
      },
    ],
  })
}

export async function initNativeOrderAlerts(): Promise<void> {
  await ensureInitialized()
}

export async function startOrderAlertService(): Promise<void> {
  if (!isAndroidNative()) return
  if (serviceRunning) {
    console.info('[order-alerts] foreground service already running')
    return
  }

  await ensureInitialized()
  setRuntimeError(null)

  try {
    const { ForegroundService } = await import(
      '@capawesome-team/capacitor-android-foreground-service'
    )
    await ForegroundService.startForegroundService({
      id: FG_SERVICE_NOTIFICATION_ID,
      title: 'Wortelogy POS',
      body: 'Listening for orders',
      smallIcon: 'ic_stat_order',
      notificationChannelId: LISTENING_CHANNEL_ID,
      serviceType: FG_SERVICE_TYPE_DATA_SYNC,
      silent: true,
    })
    serviceRunning = true
    console.info('[order-alerts] foreground service started')
  } catch (err) {
    serviceRunning = false
    const msg = err instanceof Error ? err.message : String(err)
    setRuntimeError(`Background listener failed: ${msg}`)
    console.error('[order-alerts] foreground service start failed:', err)
    throw err
  }
}

export async function stopOrderAlertService(): Promise<void> {
  if (!isAndroidNative() || !serviceRunning) return
  try {
    const { ForegroundService } = await import(
      '@capawesome-team/capacitor-android-foreground-service'
    )
    await ForegroundService.stopForegroundService()
    console.info('[order-alerts] foreground service stopped')
  } finally {
    serviceRunning = false
  }
}

async function scheduleOrderNotification(orders: Order[]): Promise<void> {
  await ensureInitialized()
  const { LocalNotifications } = await import('@capacitor/local-notifications')
  const title = orders.length === 1 ? 'New order' : `${orders.length} new orders`
  const id = (Date.now() % 2147483646) + 1

  await LocalNotifications.schedule({
    notifications: [
      {
        id,
        title,
        body: formatOrderAlertBody(orders),
        channelId: ORDER_ALERTS_CHANNEL_ID,
        smallIcon: 'ic_stat_order',
        autoCancel: true,
        extra: { placeId: getCurrentPlaceId() },
      },
    ],
  })
}

export async function alertNewOrders(orders: Order[]): Promise<boolean> {
  if (!isAndroidNative() || orders.length === 0) return false

  const volume = getSettings().newOrderChimeVolume
  if (volume <= 0) return false

  const uiActive = isAppUiActive()
  const catchUp = uiActive && isWithinCatchUpWindow()
  const pushFirstMode = Boolean(activePushPlaceId)

  if (!uiActive) {
    if (pushFirstMode) {
      // FCM is the primary background path; avoid duplicate local notifications.
      return false
    }
    try {
      await scheduleOrderNotification(orders)
      return true
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      setRuntimeError(`Order notification failed: ${msg}`)
      console.error('[order-alerts] background notification failed:', err)
      return false
    }
  }

  if (catchUp) {
    try {
      await scheduleOrderNotification(orders)
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      setRuntimeError(`Order notification failed: ${msg}`)
      console.error('[order-alerts] catch-up notification failed:', err)
    }
    playNewOrderChime()
    return true
  }

  playNewOrderChime()
  return true
}
